//
// # HierarchyPanel
//
// IDE sidebar panel showing the instance hierarchy of the active design.
// Displays instances in a tree structure with lazy-loaded child expansion.
//
// - Click: select instance in viewport
// - Double-click: select and zoom to instance
// - Hover: highlight instance on canvas
// - Responds to "Show in Hierarchy" context menu reveal target
// - Filter: searches recursively through the hierarchy (BFS, capped at 100)
//

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Network, Search, X } from "lucide-react";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { usePersistentScroll } from "../common/usePersistentScroll";
import { useHierarchyData } from "./useHierarchyData";
import type { HierarchySearchResult } from "./useHierarchyData";
import { HierarchyTreeRow } from "./HierarchyTreeRow";
import { HierarchySearchRow } from "./HierarchySearchRow";
import "./hierarchyPanel.css";

// Maximum number of search results to display
const MAX_SEARCH_RESULTS = 100;

// Debounce delay for search input (ms)
const SEARCH_DEBOUNCE_MS = 300;

interface HierarchyPanelProps {
  // Whether the panel's dockview tab is currently the visible one. When hidden
  // the tree skips its rebuild work; it refreshes from the live model as soon
  // as the tab becomes visible again. Defaults to true so the panel still works
  // if rendered outside a dockview tab.
  readonly visible?: boolean;
}

export function HierarchyPanel({ visible = true }: HierarchyPanelProps = {}) {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const { entries, contentKind, loadChildren, searchHierarchy } =
    useHierarchyData(visible);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Preserve the tree's scroll position across dockview tab switches.
  usePersistentScroll(scrollContainerRef);
  const searchRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState("");

  // Currently selected row key (persistent highlight, like depot tree).
  // Keys encode the full ancestry path so IDs that are only unique within
  // a single design don't collide across tree levels.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Search state
  const [searchResults, setSearchResults] = useState<
    ReadonlyArray<HierarchySearchResult>
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchComplete, setSearchComplete] = useState(false);

  // Handle "Show in Hierarchy" reveal target — select and scroll to the instance
  const revealTarget = snapshot.hierarchyRevealTarget;

  useEffect(() => {
    if (!revealTarget) return;

    // Select the instance immediately (highlight will appear once the row renders).
    // Top-level rows use "name:id" as their key (no parent prefix).
    setSelectedKey(`${revealTarget.name}:${revealTarget.id}`);

    // Clear the reveal request from workspace so it doesn't re-trigger
    workspace.clearHierarchyReveal();
  }, [revealTarget, workspace]);

  // Scroll to the selected row when it appears in the DOM.
  // Runs when selectedKey or entries change (entries may still be loading).
  useEffect(() => {
    if (selectedKey === null) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const row = container.querySelector(
      `[data-hierarchy-key="${CSS.escape(selectedKey)}"]`
    );
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedKey, entries]);

  // Debounced recursive search
  useEffect(() => {
    if (!filter) {
      setSearchResults([]);
      setIsSearching(false);
      setSearchComplete(false);
      return;
    }

    setIsSearching(true);
    setSearchComplete(false);

    const abortController = new AbortController();

    const timer = setTimeout(() => {
      void searchHierarchy(filter, MAX_SEARCH_RESULTS, abortController.signal)
        .then((results) => {
          if (!abortController.signal.aborted) {
            setSearchResults(results);
            setIsSearching(false);
            setSearchComplete(true);
          }
        })
        .catch(() => {
          if (!abortController.signal.aborted) {
            setIsSearching(false);
            setSearchComplete(true);
          }
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [filter, searchHierarchy]);

  const clearFilter = () => {
    setFilter("");
    searchRef.current?.focus();
  };

  const hasEntries = entries.length > 0;
  const isFiltering = filter.length > 0;

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Panel header */}
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] select-none border-b border-[var(--color-ide-border)]">
        <div className="flex items-center gap-1.5">
          <Network size={11} />
          <span className="flex-1">Hierarchy</span>
          {hasEntries && (
            <span className="hierarchy-count">{entries.length}</span>
          )}
        </div>
      </div>

      {/* Search row */}
      {hasEntries && (
        <div className="px-2 py-1.5 border-b border-[var(--color-ide-border)]">
          <div className="flex flex-1 items-center gap-1.5 bg-[var(--color-ide-bg)] border border-[var(--color-ide-border)] rounded px-2 py-1">
            <Search
              size={11}
              className="text-[var(--color-ide-text-muted)] shrink-0"
            />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search instances..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="flex-1 bg-transparent text-xs text-[var(--color-ide-text)] placeholder-[var(--color-ide-text-dim)] outline-none font-mono"
            />
            {filter && (
              <button
                onClick={clearFilter}
                className="text-[var(--color-ide-text-muted)] hover:text-[var(--color-ide-text)]"
              >
                <X size={11} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Content area */}
      <div
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
        ref={scrollContainerRef}
      >
        {!contentKind && (
          <div className="hierarchy-empty">
            Open a design to view its hierarchy.
          </div>
        )}

        {contentKind && !hasEntries && (
          <div className="hierarchy-empty">No instances in this design.</div>
        )}

        {/* Searching indicator */}
        {isFiltering && isSearching && (
          <div className="hierarchy-empty">Searching...</div>
        )}

        {/* Search results (flat list with paths) */}
        {isFiltering && !isSearching && searchComplete && (
          <>
            {searchResults.length === 0 && (
              <div className="hierarchy-empty">No matching instances.</div>
            )}
            {searchResults.length > 0 && (
              <>
                {searchResults.length >= MAX_SEARCH_RESULTS && (
                  <div className="hierarchy-search-info">
                    Showing first {MAX_SEARCH_RESULTS} results.
                  </div>
                )}
                {searchResults.map((result, idx) => (
                  <HierarchySearchRow
                    key={`${idx}-${result.path.join("/")}-${result.name}-${
                      result.id
                    }`}
                    result={result}
                    selectedKey={selectedKey}
                    onSelect={setSelectedKey}
                  />
                ))}
              </>
            )}
          </>
        )}

        {/* Normal tree view (no filter active) */}
        {!isFiltering &&
          entries.map((entry) => (
            <HierarchyTreeRow
              // Key on the stable name+id, not the list index: entries are
              // re-sorted on every edit, so an index-based key would remount
              // (and collapse) every row after an insertion.
              key={`${entry.name}:${entry.id}`}
              name={entry.name}
              id={entry.id}
              targetIdent={entry.targetIdent}
              targetDisplayName={entry.targetDisplayName}
              entityRef={entry.entityRef}
              indentLevel={0}
              parentKey=""
              loadChildren={loadChildren}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              activeIdent={snapshot.activeIdent}
            />
          ))}
      </div>
    </div>
  );
}
